import type { CompanyInsuranceParts } from '@/utils/nontaxBeforeCompanyIns';
import type { OrdinaryWageRiskBreakdown } from '@/utils/ordinaryWageRisk';
import type {
  MarginalSavingBreakdown,
  MarginalSavingParts,
  NontaxImprovementBreakdown,
} from '@/utils/nontaxImprovementSaving';
import { NONTAX_ONH_MONTHLY_LIMIT } from '@/utils/nontaxImprovementSaving';

export interface NontaxDiffTooltipSet {
  nonTax: string;
  companyIns: string;
  retirement: string;
  netPay: string;
  saving: string;
  ordinaryWageRisk: string;
}

interface TaxRateLike {
  tr_yeon: number;
  tr_gun: number;
  tr_jang: number;
  tr_go: number;
  tr_sil: number;
  tr_san: number;
}

export type { TaxRateLike };

export interface BeforeDeductionParts {
  l_yeon: number;
  l_gun: number;
  l_jang: number;
  l_go: number;
  l_so: number;
  l_ji: number;
  l_jiwon: number;
  l_sik: number;
  l_etc: number;
  l_yearEnd: number;
  l_total: number;
}

const ac = (n: number): string => Math.round(Number(n) || 0).toLocaleString();

const line = (text: string) => text;

const buildNonTaxDiffTooltip = (p: {
  beforeNonTax: number;
  afterNonTax: number;
  diffNonTax: number;
  beforeParts: { label: string; amount: number }[];
  afterParts: { label: string; amount: number }[];
  total: number;
  afterTaxable: number;
}): string => {
  const beforeLines = p.beforeParts
    .filter((x) => x.amount > 0)
    .map((x) => `  + ${x.label} ${ac(x.amount)}원`);
  if (beforeLines.length === 0) beforeLines.push('  (비과세 항목 없음)');

  const afterLines = p.afterParts
    .filter((x) => x.amount > 0)
    .map((x) => `  + ${x.label} ${ac(x.amount)}원`);
  if (afterLines.length === 0) {
    afterLines.push(`  총급여 ${ac(p.total)} − 과세 ${ac(p.afterTaxable)}`);
  } else {
    afterLines.push(`  (합계 ${ac(p.afterNonTax)}원, 총급여 ${ac(p.total)} − 과세 ${ac(p.afterTaxable)})`);
  }

  return [
    line('【비과세 차액】 미적용 − 적용(현재)'),
    line(`차액: ${ac(p.diffNonTax)}원`),
    line(''),
    line(`① 미적용 비과세: ${ac(p.beforeNonTax)}원`),
    line('  → 10원 단위 절사'),
    ...beforeLines,
    line(''),
    line(`② 적용(현재) 비과세: ${ac(p.afterNonTax)}원`),
    ...afterLines,
    line(''),
    line(`③ 차액 = ${ac(p.beforeNonTax)} − ${ac(p.afterNonTax)} = ${ac(p.diffNonTax)}원`),
  ].join('\n');
};

const buildCompanyInsDiffTooltip = (p: {
  diffCompanyIns: number;
  beforeTaxable: number;
  afterTaxable: number;
  companyIns?: {
    before: CompanyInsuranceParts;
    after: CompanyInsuranceParts;
  };
}): string => {
  const before = p.companyIns?.before || {
    c_yeon: 0,
    c_gun: 0,
    c_jang: 0,
    c_go: 0,
    c_sil: 0,
    c_san: 0,
  };
  const after = p.companyIns?.after || {
    c_yeon: 0,
    c_gun: 0,
    c_jang: 0,
    c_go: 0,
    c_sil: 0,
    c_san: 0,
  };
  const taxableDiff = p.beforeTaxable - p.afterTaxable;

  const insItems = [
    { label: '건강보험', before: before.c_gun, after: after.c_gun },
    { label: '장기요양', before: before.c_jang, after: after.c_jang },
    { label: '고용보험', before: before.c_go, after: after.c_go },
    { label: '산재보험', before: before.c_sil, after: after.c_sil },
    { label: '임금채권', before: before.c_san, after: after.c_san },
  ];

  const changedItems = insItems
    .map((item) => ({ ...item, diff: item.before - item.after }))
    .filter((item) => item.diff !== 0);

  const lines = [
    line(`【기관부담 차액】 ${ac(p.diffCompanyIns)}원`),
    line(''),
  ];

  if (taxableDiff !== 0) {
    const dir = taxableDiff > 0 ? '감소' : '증가';
    lines.push(
      line(`비과세 적용 → 과세 ${ac(Math.abs(taxableDiff))}원 ${dir}`),
      line(`  ${ac(p.beforeTaxable)} → ${ac(p.afterTaxable)}`),
      line(''),
    );
  } else {
    lines.push(line('과세 변동 없음'), line(''));
  }

  if (changedItems.length > 0) {
    lines.push(line('줄어든 과세에 따른 기관부담:'));
    changedItems.forEach((item) => {
      const sign = item.diff > 0 ? '−' : '+';
      lines.push(line(`  ${item.label}  ${sign}${ac(Math.abs(item.diff))}원`));
    });
  } else {
    lines.push(line('기관부담 변동 없음'));
  }

  return lines.join('\n');
};

const buildRetirementDiffTooltip = (p: {
  diffRetirement: number;
  beforeRetirement: number;
  afterRetirement: number;
  total: number;
  uploadLunch: number;
}): string => {
  const base = p.total - p.uploadLunch;
  return [
    line('【퇴직금 차액】 미적용 − 적용(현재)'),
    line(`차액: ${ac(p.diffRetirement)}원`),
    line(''),
    line(`① 미적용 퇴직금: ${ac(p.beforeRetirement)}원`),
    line(`  = floor((총급여 ${ac(p.total)} − 중식비 ${ac(p.uploadLunch)}) ÷ 12 ÷ 10) × 10`),
    line(`  = floor(${ac(base)} ÷ 12 ÷ 10) × 10`),
    line(''),
    line(`② 적용(현재) 퇴직금: ${ac(p.afterRetirement)}원`),
    line(''),
    line(`③ 차액 = ${ac(p.beforeRetirement)} − ${ac(p.afterRetirement)} = ${ac(p.diffRetirement)}원`),
  ].join('\n');
};

const formatDeductionDetail = (parts: BeforeDeductionParts | undefined, title: string): string[] => {
  if (!parts) return [`${title}: (없음)`];
  const items: string[] = [];
  if (parts.l_yeon) items.push(`  + 국민연금 ${ac(parts.l_yeon)}`);
  if (parts.l_gun) items.push(`  + 건강보험 ${ac(parts.l_gun)}`);
  if (parts.l_jang) items.push(`  + 장기요양 ${ac(parts.l_jang)}`);
  if (parts.l_go) items.push(`  + 고용보험 ${ac(parts.l_go)}`);
  if (parts.l_so) items.push(`  + 소득세 ${ac(parts.l_so)}`);
  if (parts.l_ji) items.push(`  + 지방소득세 ${ac(parts.l_ji)}`);
  if (parts.l_jiwon) items.push(`  + 지원금/부담 ${ac(parts.l_jiwon)}`);
  if (parts.l_sik) items.push(`  + 중식비공제 ${ac(parts.l_sik)}`);
  if (parts.l_etc) items.push(`  + 기타공제 ${ac(parts.l_etc)}`);
  if (parts.l_yearEnd) items.push(`  + 연말정산 ${ac(parts.l_yearEnd)}`);
  return [`${title} 공제합계: ${ac(parts.l_total)}원`, ...items];
};

const buildNetPayDiffTooltip = (p: {
  diffNetPay: number;
  beforeNetPay: number;
  afterNetPay: number;
  total: number;
  beforeDeduction: number;
  afterDeduction: number;
  beforeDeductionParts?: BeforeDeductionParts;
  afterDeductionParts?: BeforeDeductionParts;
  beforeTaxable: number;
  afterTaxable: number;
}): string => {
  const beforeLines = formatDeductionDetail(p.beforeDeductionParts, '① 미적용');
  const afterLines = formatDeductionDetail(p.afterDeductionParts, '② 적용(현재)');

  return [
    line('【실수령 차액】 미적용 − 적용(현재)'),
    line(`차액: ${ac(p.diffNetPay)}원 (미적용 시 공제↑ → 실수령↓)`),
    line(''),
    line(`미적용 과세: ${ac(p.beforeTaxable)}원 / 적용 과세: ${ac(p.afterTaxable)}원`),
    line(''),
    ...beforeLines,
    line(`  → 실수령 = 총급여 ${ac(p.total)} − ${ac(p.beforeDeduction)} = ${ac(p.beforeNetPay)}원`),
    line(''),
    ...afterLines,
    line(`  → 실수령 = 총급여 ${ac(p.total)} − ${ac(p.afterDeduction)} = ${ac(p.afterNetPay)}원`),
    line(''),
    line(`③ 차액 = ${ac(p.beforeNetPay)} − ${ac(p.afterNetPay)} = ${ac(p.diffNetPay)}원`),
  ].join('\n');
};

const buildOrdinaryWageRiskTooltip = (breakdown?: OrdinaryWageRiskBreakdown): string => {
  if (!breakdown || breakdown.monthlyRisk <= 0) {
    return [
      line('【통상임금 위험액】 당월'),
      line('추가 부담 없음 (통상임금 상승 미발생)'),
    ].join('\n');
  }

  const pct = breakdown.ratio > 1
    ? ((breakdown.ratio - 1) * 100).toFixed(1)
    : '0';

  return [
    line('【통상임금 위험액】 당월'),
    line(`당월 위험액: ${ac(breakdown.monthlyRisk)}원`),
    line(''),
    line(`통상임금: ${ac(breakdown.usualBefore)} → ${ac(breakdown.usualAfter)} (+${pct}%)`),
    line(`연장·야간 가산 인상분 (RISK A)`),
    line(''),
    line(`참고 · 1년 추정: ${ac(breakdown.annualOvertimeNight + breakdown.annualSeverance + breakdown.annualLeave)}원`),
    line(`참고 · 3년 추정: ${ac(breakdown.risk3Year)}원`),
  ].join('\n');
};

const buildSavingPartLines = (parts: MarginalSavingParts): string[] => {
  const lines: string[] = [];
  const add = (label: string, amount: number) => {
    if (amount > 0) lines.push(line(`  ${label}: ${ac(amount)}원`));
  };
  add('건강보험(기관)', parts.employerGun);
  add('장기요양(기관)', parts.employerJang);
  add('고용보험(기관)', parts.employerGo);
  add('산재보험(기관)', parts.employerSil);
  add('임금채권(기관)', parts.employerSan);
  add('건강보험(근로자)', parts.employeeGun);
  add('장기요양(근로자)', parts.employeeJang);
  add('고용보험(근로자)', parts.employeeGo);
  add('소득세', parts.employeeSo);
  add('지방세', parts.employeeJi);
  add('퇴직금(월)', parts.retirement);
  return lines;
};

const buildSavingTooltip = (p: {
  saving: number;
  improvement: NontaxImprovementBreakdown;
  savingBreakdown: MarginalSavingBreakdown;
  isTotal?: boolean;
}): string => {
  const { improvement, savingBreakdown } = p;
  const lines = [
    line('【절감액】 비과세 개선액에 대한 4대보험·세금 절감'),
    line(`절감액: ${ac(p.saving)}원`),
    line(''),
    line(p.isTotal ? '비과세 개선 내역 (합계)' : '비과세 개선 내역'),
  ];

  if (improvement.lunchImprovement > 0) {
    lines.push(
      line(`  중식비: 기존 ${ac(improvement.uploadLunch)}원 → 개선 후 ${ac(improvement.improvedLunch)}원`),
      line(`    → 중식비 비과세 개선 ${ac(improvement.lunchImprovement)}원`),
    );
  } else if (improvement.uploadLunch > 0 || improvement.improvedLunch > 0) {
    lines.push(line(`  중식비: 개선 없음 (기존 ${ac(improvement.uploadLunch)}원)`));
  }

  if (improvement.onhImprovement > 0) {
    lines.push(
      line(`  연·야·휴 합계: ${ac(improvement.onhGross)}원`),
      line(`    → 비과세 적용 ${ac(improvement.onhImprovement)}원 (월 최대 ${ac(NONTAX_ONH_MONTHLY_LIMIT)}원)`),
    );
  } else if (improvement.onhGross > 0) {
    lines.push(line(`  연·야·휴: 개선 전 비과세 0원 (합계 ${ac(improvement.onhGross)}원)`));
  }

  lines.push(line(`  비과세 개선 합계: ${ac(improvement.totalImprovement)}원`));
  lines.push(line(''), line('절감 산출 (해당자 부담·가입 항목만)'));

  const partLines = buildSavingPartLines(savingBreakdown.parts);
  if (partLines.length === 0) {
    lines.push(line('  (적용 대상 없음)'));
  } else {
    lines.push(...partLines);
  }

  lines.push(
    line(''),
    line(`합계: ${ac(savingBreakdown.total)}원`),
    line(''),
    line('※ 국민연금·고용보험 미가입자는 해당 요율을 제외하고 계산'),
  );

  return lines.join('\n');
};

export const aggregateSavingContext = (
  items: Array<{
    improvement: NontaxImprovementBreakdown;
    savingBreakdown: MarginalSavingBreakdown;
  }>,
): {
  saving: number;
  improvement: NontaxImprovementBreakdown;
  savingBreakdown: MarginalSavingBreakdown;
} => {
  const improvement: NontaxImprovementBreakdown = {
    uploadLunch: 0,
    improvedLunch: 0,
    lunchImprovement: 0,
    onhGross: 0,
    onhImprovement: 0,
    totalImprovement: 0,
  };
  const parts: MarginalSavingParts = {
    employerGun: 0,
    employerJang: 0,
    employerGo: 0,
    employerSil: 0,
    employerSan: 0,
    employeeGun: 0,
    employeeJang: 0,
    employeeGo: 0,
    employeeSo: 0,
    employeeJi: 0,
    retirement: 0,
  };

  let saving = 0;
  for (const item of items) {
    saving += item.savingBreakdown.total;
    improvement.lunchImprovement += item.improvement.lunchImprovement;
    improvement.onhGross += item.improvement.onhGross;
    improvement.onhImprovement += item.improvement.onhImprovement;
    improvement.totalImprovement += item.improvement.totalImprovement;
    for (const key of Object.keys(parts) as Array<keyof MarginalSavingParts>) {
      parts[key] += item.savingBreakdown.parts[key];
    }
  }

  const employerTotal =
    parts.employerGun +
    parts.employerJang +
    parts.employerGo +
    parts.employerSil +
    parts.employerSan;
  const employeeTotal =
    parts.employeeGun +
    parts.employeeJang +
    parts.employeeGo +
    parts.employeeSo +
    parts.employeeJi;

  return {
    saving,
    improvement,
    savingBreakdown: {
      parts,
      employerTotal,
      employeeTotal,
      retirement: parts.retirement,
      total: saving,
    },
  };
};

export const buildRowDiffTooltips = (ctx: {
  nonTax: Parameters<typeof buildNonTaxDiffTooltip>[0];
  companyIns: Parameters<typeof buildCompanyInsDiffTooltip>[0];
  retirement: Parameters<typeof buildRetirementDiffTooltip>[0];
  netPay: Parameters<typeof buildNetPayDiffTooltip>[0];
  saving: Parameters<typeof buildSavingTooltip>[0];
  ordinaryWageRisk?: OrdinaryWageRiskBreakdown;
}): NontaxDiffTooltipSet => ({
  nonTax: buildNonTaxDiffTooltip(ctx.nonTax),
  companyIns: buildCompanyInsDiffTooltip(ctx.companyIns),
  retirement: buildRetirementDiffTooltip(ctx.retirement),
  netPay: buildNetPayDiffTooltip(ctx.netPay),
  saving: buildSavingTooltip(ctx.saving),
  ordinaryWageRisk: buildOrdinaryWageRiskTooltip(ctx.ordinaryWageRisk),
});

export interface NontaxTotalsDiffSums {
  beforeNonTax: number;
  afterNonTax: number;
  beforeCompanyIns: number;
  afterCompanyIns: number;
  beforeRetirement: number;
  afterRetirement: number;
  beforeNetPay: number;
  afterNetPay: number;
  ordinaryWageRisk: number;
  ordinaryWageRisk1Year: number;
  ordinaryWageRiskMonthly: number;
}

export interface NontaxTotalsDiff {
  nonTax: number;
  companyIns: number;
  retirement: number;
  netPay: number;
  saving: number;
}

export const buildTotalsDiffTooltips = (
  sums: NontaxTotalsDiffSums,
  diff: NontaxTotalsDiff,
  savingContext?: {
    saving: number;
    improvement: NontaxImprovementBreakdown;
    savingBreakdown: MarginalSavingBreakdown;
  },
): NontaxDiffTooltipSet => ({
  nonTax: [
    line('【합계 · 비과세 차액】'),
    line(`차액: ${ac(diff.nonTax)}원`),
    line(`  미적용 합계: ${ac(sums.beforeNonTax)}원`),
    line(`  적용 합계: ${ac(sums.afterNonTax)}원`),
    line(''),
    line('개별 직원 행에 마우스를 올리면 항목별 산출 내역을 볼 수 있습니다.'),
  ].join('\n'),
  companyIns: [
    line('【합계 · 기관부담 차액】'),
    line(`차액: ${ac(diff.companyIns)}원`),
    line(''),
    line('비과세 적용으로 과세가 줄면 기관부담 4대보험도 함께 줄어듭니다.'),
    line('개별 행에 마우스를 올리면 과세 감소액과 항목별 차액을 볼 수 있습니다.'),
  ].join('\n'),
  retirement: [
    line('【합계 · 퇴직금 차액】'),
    line(`차액: ${ac(diff.retirement)}원`),
    line(`  미적용 합계: ${ac(sums.beforeRetirement)}원`),
    line(`  적용 합계: ${ac(sums.afterRetirement)}원`),
  ].join('\n'),
  netPay: [
    line('【합계 · 실수령 차액】'),
    line(`차액: ${ac(diff.netPay)}원`),
    line(`  미적용 합계: ${ac(sums.beforeNetPay)}원`),
    line(`  적용 합계: ${ac(sums.afterNetPay)}원`),
  ].join('\n'),
  saving: savingContext
    ? buildSavingTooltip({
        saving: savingContext.saving,
        improvement: savingContext.improvement,
        savingBreakdown: savingContext.savingBreakdown,
        isTotal: true,
      })
    : buildSavingTooltip({
        saving: diff.saving,
        improvement: {
          uploadLunch: 0,
          improvedLunch: 0,
          lunchImprovement: 0,
          onhGross: 0,
          onhImprovement: 0,
          totalImprovement: 0,
        },
        savingBreakdown: {
          parts: {
            employerGun: 0,
            employerJang: 0,
            employerGo: 0,
            employerSil: 0,
            employerSan: 0,
            employeeGun: 0,
            employeeJang: 0,
            employeeGo: 0,
            employeeSo: 0,
            employeeJi: 0,
            retirement: diff.retirement,
          },
          employerTotal: diff.companyIns,
          employeeTotal: 0,
          retirement: diff.retirement,
          total: diff.saving,
        },
      }),
  ordinaryWageRisk: [
    line('【합계 · 통상임금 위험액 · 당월】'),
    line(`합계: ${ac(sums.ordinaryWageRiskMonthly)}원`),
    line(''),
    line(`참고 · 1년 추정치: ${ac(sums.ordinaryWageRisk1Year)}원`),
    line(`참고 · 3년 추정치: ${ac(sums.ordinaryWageRisk)}원`),
    line(''),
    line('개별 행에 마우스를 올리면 산출 내역을 볼 수 있습니다.'),
  ].join('\n'),
});
